import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ConfirmationDialog from './ConfirmationDialog';

interface FinalizeNominationsButtonProps {
  maxEvaluators: number;
  currentEvaluators: number;
  allEvaluationsCompleted: boolean;
  allContextualized: boolean;
  onFinalize: () => void;
}

export default function FinalizeNominationsButton({
  maxEvaluators,
  currentEvaluators,
  allEvaluationsCompleted,
  allContextualized,
  onFinalize
}: FinalizeNominationsButtonProps) {
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Check all conditions
  const meetsMinimumRequirement = currentEvaluators >= 5;
  const underMaxLimit = currentEvaluators <= 10;
  const isEnabled = meetsMinimumRequirement && 
                   underMaxLimit && 
                   allEvaluationsCompleted && 
                   allContextualized;

  // Only show button if plan allows more than 5 evaluators
  if (maxEvaluators <= 5) return null;

  return (
    <>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-8"
      >
        <button
          onClick={() => setShowConfirmation(true)}
          disabled={!isEnabled}
          className={`w-full py-3 px-6 rounded-lg text-center font-medium transition-colors ${
            isEnabled
              ? 'bg-primary text-dark hover:bg-primary-hover'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          Finalize Nominations
        </button>

        {!isEnabled && (
          <p className="mt-2 text-sm text-gray-500 text-center">
            {!meetsMinimumRequirement && 'Nominate at least 5 evaluators. '}
            {!allEvaluationsCompleted && 'Wait for all evaluations to be completed. '}
            {!allContextualized && 'Provide context for all evaluators.'}
          </p>
        )}
      </motion.div>

      <ConfirmationDialog
        isOpen={showConfirmation}
        title="Finalize Nominations"
        message="You can finalize the nomination process if you have nominated at least 5 evaluators. By doing so, you confirm that you are ready to proceed with the feedback collection, even if fewer than the maximum number of evaluators are nominated. Once finalized, no additional evaluators can be added."
        confirmLabel="Finalize"
        onConfirm={() => {
          onFinalize();
          setShowConfirmation(false);
        }}
        onCancel={() => setShowConfirmation(false)}
      />
    </>
  );
}